import { Eye, EyeOff, FileText, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { DeliveryProofHtmlPreview } from './DeliveryProofHtmlPreview';
import { useDpPdfPreviewStore } from '../stores/dpPdfPreviewStore';
import type { DeliveryProofFormData } from '@/features/delivery-proof/stores/deliveryProofStore';
import type { AvailableWarranty } from '@/features/delivery-proof/hooks/useAvailableWarranties';
import type { Breakpoint } from '@/hooks/useBreakpoint';

interface DeliveryProofPdfPreviewPanelProps {
  formData: DeliveryProofFormData;
  beneficiary?: Record<string, any>;
  vehicle?: Record<string, any>;
  companyName?: string;
  companySiren?: string;
  companyAddress?: string;
  companyCpCity?: string;
  companyPhone?: string;
  companyEmail?: string;
  vendorType?: string;
  logoUrl?: string | null;
  availableWarranties?: AvailableWarranty[];
  currentStep?: number;
  breakpoint: Breakpoint;
}

/** Desktop panel width in pixels */
export const DP_PDF_PANEL_WIDTH = 600;

/** Width of the tab button attached to the panel's left edge */
export const DP_TAB_WIDTH = 56;

/**
 * Fixed right panel showing a live HTML preview of the delivery proof PDF.
 *
 * - Desktop (lg+): side-by-side panel with dynamic width
 * - Mobile / Tablet (< lg): full-screen overlay triggered by a FAB
 */
export function DeliveryProofPdfPreviewPanel({
  formData,
  beneficiary,
  vehicle,
  companyName,
  companySiren,
  companyAddress,
  companyCpCity,
  companyPhone,
  companyEmail,
  vendorType,
  logoUrl,
  availableWarranties,
  currentStep,
  breakpoint,
}: DeliveryProofPdfPreviewPanelProps) {
  const { isOpen, toggle, close } = useDpPdfPreviewStore();
  const isDesktop = breakpoint === 'lg';

  // On desktop: 45% of viewport capped between 380 and 700px
  const panelWidthPx = (() => {
    if (!isDesktop) return 0;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return Math.min(DP_PDF_PANEL_WIDTH, Math.max(340, Math.round(vw * 0.38)));
  })();

  const previewContent = (
    <DeliveryProofHtmlPreview
      formData={formData}
      beneficiary={beneficiary}
      vehicle={vehicle}
      companyName={companyName}
      companySiren={companySiren}
      companyAddress={companyAddress}
      companyCpCity={companyCpCity}
      companyPhone={companyPhone}
      companyEmail={companyEmail}
      vendorType={vendorType}
      logoUrl={logoUrl}
      availableWarranties={availableWarranties}
      currentStep={currentStep}
    />
  );

  // ===================== MOBILE / TABLET : overlay plein écran =====================
  if (!isDesktop) {
    return (
      <>
        {/* FAB — floating action button en bas à droite, au-dessus du footer */}
        <button
          type="button"
          onClick={toggle}
          className="tw-fixed tw-bottom-24 tw-right-4 tw-z-50 tw-w-14 tw-h-14 tw-rounded-full tw-bg-affinicar-primary tw-text-white tw-shadow-lg tw-flex tw-items-center tw-justify-center hover:tw-bg-affinicar-dark tw-transition-colors"
          title={isOpen ? "Fermer l'aperçu" : "Aperçu de l'attestation"}
        >
          {isOpen ? <EyeOff className="tw-w-6 tw-h-6" /> : <Eye className="tw-w-6 tw-h-6" />}
        </button>

        {/* Overlay backdrop */}
        {isOpen && (
          <div
            className="tw-fixed tw-inset-0 tw-z-40 tw-bg-black/50"
            onClick={close}
          />
        )}

        {/* Full-screen panel */}
        <div
          className={`tw-fixed tw-inset-0 tw-top-16 tw-z-50 tw-flex tw-flex-col tw-bg-gray-50 tw-transition-transform tw-duration-300 tw-ease-in-out ${isOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'} ${isOpen ? 'tw-pointer-events-auto' : 'tw-pointer-events-none'}`}
        >
          {/* Header bar */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-white tw-flex-shrink-0">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FileText className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-gray-900">Aperçu de l'attestation</span>
            </div>
            <button
              type="button"
              onClick={close}
              className="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>
          </div>

          {/* Preview content */}
          <div className="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-p-3">
            {previewContent}
          </div>
        </div>
      </>
    );
  }

  // ===================== DESKTOP : side-by-side panel =====================
  return (
    <div
      className="tw-fixed tw-top-16 tw-right-0 tw-h-[calc(100vh-4rem)] tw-h-[calc(100dvh-4rem)] tw-z-40 tw-transition-all tw-duration-300 tw-ease-in-out tw-flex tw-flex-col tw-bg-gray-50 tw-border-l tw-border-gray-200 tw-shadow-xl"
      style={{
        width: panelWidthPx,
        transform: isOpen ? 'translateX(0)' : `translateX(${panelWidthPx}px)`,
      }}
    >
      {/* Tab button — attached to panel's left edge, moves with panel */}
      <button
        type="button"
        onClick={toggle}
        className="tw-absolute tw-top-1/2 tw-left-0 -tw-translate-x-full -tw-translate-y-1/2 tw-z-10 tw-w-14 tw-py-4 tw-px-2 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-l-xl tw-cursor-pointer tw-transition-all tw-duration-200 tw-ease-out tw-hidden sm:tw-flex hover:tw-w-16"
        style={{
          background: isOpen
            ? 'linear-gradient(180deg, #4a52a0 0%, #6b60a0 100%)'
            : 'linear-gradient(180deg, #575fa7 0%, #8074af 100%)',
          boxShadow: '-4px 2px 16px rgba(87, 95, 167, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.1)',
        }}
        title={isOpen ? "Fermer l'aperçu" : "Aperçu attestation"}
      >
        <FileText className="tw-w-6 tw-h-6 tw-text-white" />
        <span className="tw-text-white tw-text-sm tw-font-semibold [writing-mode:vertical-lr] tw-rotate-180">
          {isOpen ? "Fermer l'aperçu" : "Aperçu attestation"}
        </span>
        {isOpen
          ? <ChevronRight className="tw-w-5 tw-h-5 tw-text-white/60" />
          : <ChevronLeft className="tw-w-5 tw-h-5 tw-text-white/60" />
        }
      </button>

      {/* HTML Preview */}
      <div className={`tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-p-3 ${isOpen ? '' : 'tw-pointer-events-none'}`}>
        {previewContent}
      </div>
    </div>
  );
}
